import React from 'react';

import {
	Box,
	Container,
	Typography,
	type SxProps,
	type Theme,
} from '@mui/material';

import RouteLink from '@ivy/components/atoms/RouteLink';
import FacilitySearchInput from '@ivy/components/organisms/FacilitySearchInput';
import { useRedirect } from '@ivy/components/providers/RedirectProvider';
import { PROFESSION2SLUG, Profession } from '@ivy/constants/clinician';
import { RouteUri } from '@ivy/constants/routes';
import { type CompleteLocation } from '@ivy/lib/services/maps';
import { buildInternalLink } from '@ivy/lib/util/route';
import useWhitelabel from '@ivy/lib/whitelabel/useWhitelabel';

const Search = ({ sx }: { sx?: SxProps<Theme> }) => {
	const redirect = useRedirect();
	const wl = useWhitelabel();

	const handleSubmit = (location: CompleteLocation) => {
		redirect({
			pathname:
				'url' in location
					? location.url
					: buildInternalLink(RouteUri.SEARCH_INDEX, {
							profession: PROFESSION2SLUG[Profession.PHYSICIAN],
							slug: 'slug' in location ? location.slug! : 'search',
					  }),
			search:
				'url' in location
					? ''
					: `&location=${encodeURIComponent(JSON.stringify(location))}`,
			whitelabel: location.type === 'org' ? undefined : 'default',
			showSplashScreen: true,
		});
	};

	return (
		<Box sx={sx}>
			<FacilitySearchInput
				mobileBp='sm'
				onSubmit={handleSubmit}
				sx={{
					width: '100%',
					mb: 2,
				}}
				types={['org', 'place', 'facility']}
				hideProfession
			/>
			<Typography
				variant='body1'
				fontStyle='italic'
				color='text.icon'
				textAlign={{
					xs: 'center',
					md: 'left',
				}}
			>
				Search powered by
				<RouteLink to={wl.default.baseUrl}>
					<Box
						component='img'
						alt={wl.default.productName}
						src={wl.default.logo}
						sx={{
							ml: 1,
							height: '2rem',
							position: 'relative',
							top: 'calc(0.2 * 2rem)',
						}}
					/>
				</RouteLink>
			</Typography>
		</Box>
	);
};

const AcepHero = () => {
	return (
		<Box
			sx={{
				position: 'relative',
			}}
		>
			<Container
				maxWidth='xl'
				sx={{
					height: {
						md: 'calc(100vh - 104px)',
					},
					minHeight: {
						md: 600,
					},
					pt: {
						xs: 8,
						md: 0,
					},
					display: 'flex',
					flexDirection: {
						xs: 'column',
						md: 'row',
					},
					justifyContent: {
						md: 'center',
					},
					alignItems: {
						md: 'center',
					},
				}}
			>
				<Box
					sx={{
						flexBasis: {
							md: '60%',
						},
					}}
				>
					<Typography variant='h1' sx={{ mb: 10 }}>
						Uncover details about{' '}
						<Box component='span' color='primary.main'>
							emergency medicine employers
						</Box>{' '}
						in the pages of ACEP Open Book
					</Typography>
					<Search />
				</Box>
				<Box
					sx={(theme) => ({
						[theme.breakpoints.down('md')]: {
							// Full bleed
							width: '100vw',
							position: 'relative',
							left: '50%',
							right: '50%',
							ml: '-50vw',
							mr: '-50vw',
							mt: 10,
							height: '300px',
						},
						[theme.breakpoints.up('md')]: {
							ml: 10,
							flexBasis: '40%',
						},
					})}
				>
					<Box
						component='video'
						autoPlay
						muted
						playsInline
						sx={(theme) => ({
							width: '100%',
							[theme.breakpoints.down('md')]: {
								// Move down slightly to close a gap with the wave
								objectFit: 'cover',
								height: '100%',
							},
							[theme.breakpoints.up('md')]: {
								height: 'auto',
								borderRadius: `${
									theme.cardShape?.borderRadius || theme.shape.borderRadius
								}px`,
							},
						})}
					>
						<source
							src='https://assets.ivyclinicians.io/videos/acep%20landing%20page.mp4'
							type='video/mp4'
						/>
					</Box>
				</Box>
			</Container>
			<Box
				component='svg'
				width='1920'
				height='75'
				viewBox='0 0 1920 75'
				fill='none'
				xmlns='http://www.w3.org/2000/svg'
				preserveAspectRatio='none'
				sx={{
					// Force an overlap
					mb: '-1px',
					position: 'absolute',
					bottom: 0,
					width: '100vw',
					zIndex: 1,
				}}
			>
				<path
					d='M0 74.459L1920 74.4585C1920 74.4585 1590.3 0.000488281 1312.15 0.000488281C1034 0.000488281 919.244 60.4582 447.5 74.459C147.5 74.459 0 26.5005 0 26.5005V74.459Z'
					fill='#005EB8'
				/>
			</Box>
		</Box>
	);
};

export default AcepHero;
